import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCheck, Copy, Database, Loader2, Lock, MessageCircle, PenLine, RefreshCw, X } from 'lucide-react';
import { kycFlowService, nomineeCorrectionService, userService } from '../../../services/api';
import Snackbar from '../../common/Snackbar';
import { useIsEmployee } from './useIsEmployee';
import { SHOW_MOU_TYPE, mouTypeLabel, valueTypeLabel } from '../public/investorProfile/shared';
import { ACK_SECTION_LABELS, ACK_STATUS, BANK_GROUPS, FIELD_LABELS, KYC_GROUPS, NOMINEE_FIELD_LABELS, SECTION_STATUS, ackedStatus, fmtDate, fmtWhen, inr, createdDate } from './kycLabels';
import { RowState, VerifyHeader, VerifyRow } from './VerifyRows';
import WhatsAppLogo from './WhatsAppLogo';
import KycHistoryCard from './KycHistoryCard';
import FarmvestSyncModal from './FarmvestSyncModal';
import { DocUpload } from '../public/investorProfile/shared';
import { withCountryCode } from '../../../utils/format';

// How the client asked for a change to a verified field (routers/kyc_flow.PROOF_SOURCES).
// Nominee fields staff can edit, and the choices for the ones that are a pick-list
// (utils/kyc_flow.NOMINEE_EDIT_PROPS, RELATIONS, GENDERS).
const NOMINEE_EDITABLE = new Set(['name', 'mobile', 'relation', 'aadhar', 'gender', 'pan', 'doc_aadhar', 'doc_pan', 'doc_guardian']);
const EDIT_OPTIONS: Record<string, string[]> = {
    gender: ['Male', 'Female', 'Other'],
    relation: ['Father', 'Mother', 'Son', 'Daughter', 'Spouse', 'Brother', 'Sister', 'Guardian', 'Grand Child'],
};

const PROOF_SOURCES: { key: string; label: string }[] = [
    { key: 'email', label: 'Email' }, { key: 'whatsapp', label: 'WhatsApp' }, { key: 'call', label: 'Phone call' }, { key: 'other', label: 'Other' },
];

// Employee verification for one investor: KYC, Bank, Unit nominees and
// Acknowledgment as tabs (/kyc-queue/:mobile). Onboarding runs in three steps, one
// WhatsApp template each (utils/kyc_flow.STEP_TEMPLATES): 1 collect KYC; 2 once KYC
// is verified, add bank and a nominee per unit; 3 once those are verified. The
// acknowledgments go out separately: one ank_verify link each for KYC, bank and
// every unit's nominee, once staff verify it. Verify writes to kyc_history via
// /users/{mobile}/verify-kyc-fields; flags are staged here and sent as one
// correction request (KYC/bank → /kyc-flow, a unit's nominees → /nominee-corrections).

type Tab = 'kyc' | 'bank' | 'nominees' | 'ack';
type AckSec = 'kyc' | 'bank' | 'nominees';
// What the investor acknowledges, one link each: KYC, bank, then every unit's nominee.
const ackItems = (flow: any): string[] => [flow.acks?.kyc?.status, flow.acks?.bank?.status,
    ...(flow.units || []).map((u: any) => u.ack?.status)].filter(st => st && st !== 'n/a');
const TABS: { key: Tab; label: string }[] = [
    { key: 'kyc', label: 'KYC details' }, { key: 'bank', label: 'Bank details' },
    { key: 'nominees', label: 'Unit nominees' }, { key: 'ack', label: 'Acknowledgment' },
];
const MONO = new Set(['aadhar_number', 'pan_number', 'account_number', 'ifsc_code', 'pincode']);

const SectionChip: React.FC<{ status: string }> = ({ status }) => {
    const m = SECTION_STATUS[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
    return <span className={`h-5 px-2 inline-flex items-center rounded-full text-[11px] font-semibold whitespace-nowrap ${m.cls}`}>{m.label}</span>;
};

// A section's (or unit's) chip; once the investor acknowledged it, with the date beside it.
const AckedChip: React.FC<{ status: string; ack?: { status?: string; acknowledged_at?: string | null } }> = ({ status, ack }) => (
    <span className="inline-flex items-center gap-2 shrink-0">
        <SectionChip status={ackedStatus(status, ack?.status)} />
        {ack?.status === 'done' && ack.acknowledged_at && <span className="text-[11px] text-slate-500 whitespace-nowrap">{fmtWhen(ack.acknowledged_at)}</span>}
    </span>
);

const CARD = 'bg-white rounded-2xl ring-1 ring-[#E3E9E5]';

// Shimmer placeholder in the page's shape (profile card, section cards, table) while it loads.
const Bone: React.FC<{ className?: string }> = ({ className = '' }) => <span className={`block rounded-md bg-slate-200/80 animate-pulse ${className}`} />;
const VerifySkeleton: React.FC = () => (
    <div aria-hidden="true">
        <section className={`${CARD} p-4 sm:p-5 mt-3`}>
            <div className="flex items-center gap-3">
                <Bone className="w-11 h-11 rounded-full" />
                <div className="flex-1 space-y-2"><Bone className="h-4 w-40" /><Bone className="h-3 w-56" /></div>
                <Bone className="h-8 w-36 rounded-lg" />
            </div>
        </section>
        <section className={`${CARD} p-4 sm:p-5 mt-3`}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                {[0, 1, 2, 3].map(i => (
                    <div key={i} className="rounded-xl ring-1 ring-[#E3E9E5] p-3 space-y-2.5">
                        <div className="flex justify-between"><Bone className="h-3.5 w-24" /><Bone className="h-5 w-16 rounded-full" /></div>
                        <Bone className="h-1 w-full" />
                        <Bone className="h-3 w-32" />
                    </div>
                ))}
            </div>
        </section>
        <section className={`${CARD} mt-4 overflow-hidden`}>
            <div className="px-5 py-4 flex justify-between"><Bone className="h-4 w-32" /><Bone className="h-5 w-16 rounded-full" /></div>
            {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="px-5 py-3.5 border-t border-slate-100 flex items-center gap-4">
                    <Bone className="h-3.5 w-28" /><Bone className="h-3.5 flex-1 max-w-[260px]" />
                    <Bone className="h-5 w-16 rounded-full ml-auto" /><Bone className="h-8 w-20 rounded-lg" />
                </div>
            ))}
        </section>
    </div>
);

// Which onboarding message each section card sends. Template 2 asks for bank and
// nominees together, so both of those cards carry it.
const TILE_TEMPLATE: Record<string, 1 | 2 | 3 | undefined> = { kyc: 1, bank: 2, nominees: 2, ack: 3 };
// Header chip: just the step number.
const STEP_SHORT: Record<string, string> = { kyc: 'Step 1 of 3', bank_nominees: 'Step 2 of 3', confirm: 'Step 3 of 3', complete: 'Complete' };

const KycVerifyPage: React.FC = () => {
    const { mobile = '' } = useParams();
    const navigate = useNavigate();
    // Verifying fields and assisted entry ("Fill in for investor") are for Employees only;
    // SuperAdmin / Admin see the verification state read-only.
    const isEmployee = useIsEmployee();
    // Back to wherever staff came from (keeps the queue's filters and page); a link
    // opened directly has no history here, so fall back to the queue.
    const goBack = () => ((window.history.state?.idx ?? 0) > 0 ? navigate(-1) : navigate('/kyc-queue'));
    const [flow, setFlow] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [tab, setTab] = useState<Tab>('kyc');
    const [fvOpen, setFvOpen] = useState(false);
    const closeFv = useCallback(() => setFvOpen(false), []);
    // No order yet: bank and nominees (Template 2) wait for one, whatever the KYC state.
    const noOrder = !!flow && !flow.units?.length;
    // Bank and nominees both open once the investor submits KYC (the server stops marking
    // them "locked"); nominees only when there's an order with units to name them for.
    // Acknowledgment waits for KYC to be verified.
    const tabLocked = (k: Tab) => k === 'bank' || k === 'nominees'
        ? flow?.sections[k] === 'locked' || (k === 'nominees' && noOrder)
        : flow?.step === 'kyc' && k !== 'kyc';
    // Orders with a unit still missing its nominee (e.g. bought after KYC): one ank_nominee
    // WhatsApp per order, sent from the list in the Unit nominees section. Nominees open once KYC is submitted.
    const waitingOrders: { oid: string; missing: any[]; sent: any }[] = !flow || flow.sections.nominees === 'locked' ? [] : Array.from(new Set<string>(
        (flow.units || []).filter((u: any) => !u.allocations.length && u.order_id).map((u: any) => u.order_id as string)))
        .map((oid: string) => {
            const us = flow.units.filter((u: any) => u.order_id === oid);
            return { oid, missing: us.filter((u: any) => !u.allocations.length), sent: us.find((u: any) => u.nominee_message)?.nominee_message };
        });
    const lockReason = (k: Tab) => noOrder && (k === 'bank' || k === 'nominees') ? 'Unlocks once the investor places an order'
        : k === 'bank' || k === 'nominees' ? 'Unlocks once KYC is submitted'
        : 'Unlocks once KYC is verified';
    const tabIsLocked = tabLocked(tab);
    useEffect(() => { if (tabIsLocked) setTab('kyc'); }, [tabIsLocked]);
    // The History card follows the open section (the Acknowledgment tile keeps the last one).
    const [busy, setBusy] = useState(false);
    const [snack, setSnack] = useState<{ message: string | null; type: 'success' | 'error' | null }>({ message: null, type: null });
    const [unitId, setUnitId] = useState<string | null>(null);
    // needsProof: the field is verified (or its section acknowledged), so the change needs
    // the client's written request — a screenshot of their email or message.
    // nominee: set when editing a unit's nominee field (key is then the nominee field, e.g. "mobile").
    const [edit, setEdit] = useState<{
        key: string; label: string; value: string; reason: string; needsProof: boolean; reopens: boolean; proofUrl: string; proofSource: string;
        nominee?: { unitId: string; nomineeId: string };
    } | null>(null);
    // Verify clicks are staged here and submitted together as one batch.
    const [pendingVer, setPendingVer] = useState<Record<string, { checked: boolean; value?: string }>>({});
    const pendingCount = Object.keys(pendingVer).length;

    const toast = useCallback((message: string, type: 'success' | 'error' = 'success') => setSnack({ message, type }), []);
    const load = useCallback(async () => {
        const res = await kycFlowService.staffView(mobile);
        if (res.error) { setError(res.error); return; }
        setFlow(res.data.flow);
        setUnitId(prev => prev || res.data.flow.units?.[0]?.unit_id || null);
    }, [mobile]);
    useEffect(() => { load(); }, [load]);
    // Refresh button: reload with a visible loading state (the page dims until fresh data arrives).
    const [refreshing, setRefreshing] = useState(false);
    const refresh = async () => {
        setRefreshing(true);
        try { await load(); } finally { setRefreshing(false); }
    };

    // Stage (or un-stage) verification for keys; `current` is each key's state on the server.
    const stageVerify = (items: { key: string; checked: boolean; value?: string; current: string }[]) => {
        setPendingVer(prev => {
            const next = { ...prev };
            for (const it of items) {
                const already = it.current === 'verified';
                if (already === it.checked) delete next[it.key];   // back to what the server has
                else next[it.key] = { checked: it.checked, ...(it.value !== undefined ? { value: it.value } : {}) };
            }
            return next;
        });
    };

    const submitVerification = async () => {
        const entries = Object.entries(pendingVer);
        if (!entries.length) return;
        setBusy(true);
        let changed = 0;
        for (const checked of [true, false]) {
            const part = entries.filter(([, v]) => v.checked === checked);
            if (!part.length) continue;
            const values = Object.fromEntries(part.filter(([, v]) => v.value !== undefined).map(([k, v]) => [k, v.value as string]));
            const res = await userService.verifyKycFields(mobile, part.map(([k]) => k), checked, Object.keys(values).length ? values : undefined);
            if (res.error) { toast(res.error, 'error'); setBusy(false); await load(); return; }
            changed += res.data?.changed ?? part.length;
        }
        setPendingVer({});
        await load();
        setBusy(false);
        toast(`Verification submitted — ${changed} field${changed === 1 ? '' : 's'} updated`);
    };

    // Don't lose staged verification on reload / tab close.
    useEffect(() => {
        if (!pendingCount) return;
        const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [pendingCount]);

    const unit = useMemo(() => flow?.units?.find((u: any) => u.unit_id === unitId), [flow, unitId]);
    const openCorrection = useMemo(() => flow?.nominee_corrections?.find((c: any) => c.unit_id === unitId), [flow, unitId]);

    const cancelNomineeCorrection = async (id: string) => {
        if (!window.confirm('Cancel this correction request? The investor\'s link will stop working.')) return;
        setBusy(true);
        const res = await nomineeCorrectionService.cancel(id);
        setBusy(false);
        if (res.error) { toast(res.error, 'error'); return; }
        load();
    };

    // Templates 1 and 2 go out on WhatsApp from the server; Template 3 (no approved
    // template yet) is copied with the investor's link filled in for staff to paste.
    // Either way the server records it as sent, and by whom.
    const sendStep = async (step: 1 | 2 | 3) => {
        setBusy(true);
        const res = await kycFlowService.sendStepMessage(mobile, step);
        setBusy(false);
        if (res.error) { toast(res.error, 'error'); return; }
        if (res.data.via === 'whatsapp') { toast(`Template ${step} sent on WhatsApp to ${mobile}`); load(); return; }
        const text = String(res.data.message).replace('{link}', `${window.location.origin}${res.data.path}`);
        try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
        toast(`Template ${step} copied — paste it in WhatsApp to ${mobile}`);
        load();
    };

    // ank_nominee: WhatsApp the investor their link to add nominees for one order.
    const sendOrderNominee = async (orderId: string) => {
        setBusy(true);
        const res = await kycFlowService.sendOrderNomineeMessage(mobile, orderId);
        setBusy(false);
        if (res.error) { toast(res.error, 'error'); return; }
        toast(`Nominee link sent on WhatsApp for ${orderId}`);
        load();
    };

    // ank_verify: WhatsApp the investor the link to acknowledge one verified section,
    // or one unit's nominee.
    const sendAck = async (sec: AckSec, unitId?: string) => {
        setBusy(true);
        const res = await kycFlowService.sendAckMessage(mobile, sec, unitId);
        setBusy(false);
        if (res.error) { toast(res.error, 'error'); return; }
        toast(res.data.message || 'Acknowledgment link sent on WhatsApp');
        if (res.data.flow) setFlow(res.data.flow); else load();
    };
    const ackSendButton = (status: string, onClick: () => void) => ['ready', 'sent'].includes(status) && (
        <button type="button" disabled={busy} onClick={onClick} title="WhatsApp template “ank_verify”"
            className="h-7 px-2.5 rounded-md bg-[#25D366] hover:bg-[#1EBE5A] text-white text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-40">
            <WhatsAppLogo size={12} />{status === 'sent' ? 'Resend ack link' : 'Send ack link'}</button>
    );

    // Assisted entry: open the investor form as this staff member, for an investor
    // who can't fill it in themselves. Entries are recorded under the staff member.
    const fillForInvestor = async () => {
        setBusy(true);
        const res = await kycFlowService.createLink(mobile);
        setBusy(false);
        if (res.error) { toast(res.error, 'error'); return; }
        // Opens inside the dashboard (AssistedFillPage), not as the standalone public page.
        const token = new URLSearchParams(String(res.data.path).split('?')[1] || '').get('token') || '';
        navigate(`/kyc-queue/${mobile}/fill?token=${encodeURIComponent(token)}`);
    };

    // Per-section acknowledgment (utils/kyc_flow.section_ack): staff send each verified
    // section its own ank_verify link. Nominees are acknowledged unit by unit (unitAckBar),
    // so their bar only sums up the units.
    const ackBar = (sec: AckSec, unitAck?: any, unitLabel?: string, unitId?: string) => {
        const a = unitAck || flow?.acks?.[sec];
        // Not verified yet ("locked"): nothing to do, so no bar.
        if (!a || a.status === 'n/a' || a.status === 'locked') return null;
        const label = unitLabel || ACK_SECTION_LABELS[sec];
        const one = !!unitLabel;
        const perUnit = sec === 'nominees' && !one;
        const tone: Record<string, string> = {
            done: 'bg-emerald-50 ring-emerald-200 text-emerald-950', sent: 'bg-indigo-50 ring-indigo-200 text-indigo-950',
            ready: 'bg-[#F4FAF6] ring-[#1B4332]/30 text-[#14231B]', locked: 'bg-slate-50 ring-slate-200 text-slate-600',
        };
        const units: any[] = flow?.units || [];
        const count = (st: string) => units.filter(u => u.ack?.status === st).length;
        const waitingUnits = count('ready') + count('sent');
        const text: Record<string, string> = perUnit ? {
            done: `Investor acknowledged every unit's nominee ${fmtWhen(a.acknowledged_at)}.`,
            sent: `${count('done')} of ${units.length} units acknowledged. Send one link for all the units waiting, or pick a unit below.`,
            ready: `${count('done')} of ${units.length} units acknowledged. Send one link for all the units waiting, or pick a unit below.`,
            locked: 'Each unit\'s nominee can be acknowledged once every field of that unit is verified.',
        } : {
            done: `Investor acknowledged ${label.toLowerCase()} ${fmtWhen(a.acknowledged_at)}.`,
            sent: `Acknowledgment link sent ${fmtWhen(a.sent_at)} — waiting for the investor to confirm.`,
            ready: `${label} ${one ? 'is' : 'are'} verified. Send the investor the acknowledgment link.`,
            locked: `The investor can acknowledge ${label.toLowerCase()} once every field here is verified.`,
        };
        return (
            <div className={`rounded-xl px-3.5 py-3 ring-1 flex flex-wrap items-center gap-3 ${tone[a.status]}`}>
                <span className={`h-6 px-2.5 inline-flex items-center rounded-full text-[11px] font-bold whitespace-nowrap ${ACK_STATUS[a.status]?.cls}`}>{perUnit && a.status !== 'done' && a.status !== 'locked' ? 'Per unit' : ACK_STATUS[a.status]?.label}</span>
                <div className="flex-1 min-w-[220px] text-sm">{text[a.status]} {!perUnit && <span className="font-mono text-[11px] opacity-70">v{a.version}</span>}</div>
                {!perUnit && ackSendButton(a.status, () => sendAck(sec, unitId))}
                {/* Two or more units verified and waiting: one link the investor can acknowledge them all with. */}
                {perUnit && waitingUnits >= 2 && (
                    <button type="button" disabled={busy} onClick={() => sendAck('nominees')} title="WhatsApp template “ank_verify” — one link for every unit waiting"
                        className="h-7 px-2.5 rounded-md bg-[#25D366] hover:bg-[#1EBE5A] text-white text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-40">
                        <WhatsAppLogo size={12} />Send ack link for all {waitingUnits} units</button>
                )}
            </div>
        );
    };
    // Staff always see the unit's id and MOU, even while SHOW_MOU_TYPE hides the type from investors.
    const unitTitle = (u: any) => (<>
        <h2 className="text-[15px] font-bold text-slate-900">Unit {u.unit_index + 1} <span className="text-xs font-mono text-slate-500 font-medium">{u.unit_id}</span></h2>
        {/* Highlighted: the MOU type, and whether the MOU is given (anything but true reads "not given"). */}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className={`h-6 px-2.5 inline-flex items-center rounded-full text-[11px] font-bold ring-1 ${u.mou_type ? 'bg-indigo-50 text-indigo-900 ring-indigo-200' : 'bg-slate-100 text-slate-500 ring-slate-200'}`}>
                MOU type: {mouTypeLabel(u.mou_type) || 'Not set'}</span>
            <span className={`h-6 px-2.5 inline-flex items-center rounded-full text-[11px] font-bold ring-1 ${u.farmvest?.mou_given === true ? 'bg-emerald-50 text-emerald-900 ring-emerald-200' : 'bg-red-50 text-red-800 ring-red-200'}`}>
                {u.farmvest?.mou_given === true ? 'MOU given' : 'MOU not given'}</span>
        </div>
    </>);
    const unitAckBar = (u: any) =>ackBar('nominees', u.ack, `Unit ${u.unit_index + 1} nominee`, u.unit_id);

    // Shown where the investor (not staff) has to act: details or nominees never provided.
    // Bank and nominees are collected in step 2, once KYC is verified.
    const notYet = (what: string) => (
        <div className="rounded-2xl p-4 bg-slate-50 ring-1 ring-slate-200 text-sm text-slate-700">
            {what === 'Bank details'
                ? <><b>Bank details open once the investor submits their KYC.</b> They can add and submit them while KYC is in review.</>
                : <><b>{what} are collected in step 2.</b> Verify the KYC details first, then send Template 2 — it asks the investor for bank details and one nominee per unit.</>}
        </div>
    );

    const saveEdit = async () => {
        if (!edit) return;
        setBusy(true);
        const proof = edit.proofUrl ? { url: edit.proofUrl, source: edit.proofSource || 'other' } : undefined;
        const res = edit.nominee
            ? await kycFlowService.staffEditNominee(mobile, edit.nominee.unitId, edit.nominee.nomineeId, edit.key, edit.value, edit.reason.trim(), proof)
            : await kycFlowService.staffEdit(mobile, edit.key, edit.value, edit.reason.trim(), proof);
        setBusy(false);
        if (res.error) { toast(res.error, 'error'); return; }
        setFlow(res.data.flow); setEdit(null);
        toast(edit.reopens ? 'Field updated — verify it again, then the investor confirms it again' : 'Field updated — verify it again');
    };

    if (error) return <div className="p-8 text-center text-red-700">{error}</div>;
    if (!flow) return <div className="w-full min-w-0 pb-8 sm:pb-10"><VerifySkeleton /></div>;

    const submitted = flow.stage !== 'DRAFT';
    // A section (or unit) opens for review as soon as the investor saves it complete —
    // staff don't wait for the whole profile to be submitted.
    // complete: step 2 saved by the investor but not submitted yet — not for review.
    const STILL_FILLING = ['draft', 'not_started', 'complete'];
    const reviewable = (status?: string) => !STILL_FILLING.includes(status || '');
    const locked = flow.stage === 'ACKNOWLEDGED';
    const name = `${flow.investor.first_name || ''} ${flow.investor.last_name || ''}`.trim() || mobile;
    const rowsFor = (section: 'kyc' | 'bank') => (section === 'kyc' ? flow.kyc_fields : flow.bank_fields) as any[];
    const count = (rows: any[]) => {
        const rel = rows.filter(r => r.value && r.verify_state !== 'n/a');
        return { total: rel.length, ver: rel.filter(r => r.verify_state === 'verified').length };
    };
    const unitsVerified = flow.units.filter((u: any) => u.status === 'verified').length;
    // When each section reached staff: KYC is submitted in step 1, bank and nominees
    // together in step 2; the acknowledgment card shows when the profile completed.
    const tileWhen: Record<Tab, string> = {
        kyc: flow.investor.submitted_at ? `Submitted ${fmtWhen(flow.investor.submitted_at)}`
            : flow.investor.legacy && submitted ? 'Collected before the investor link' : 'Not submitted yet',
        // Bank and nominees are each submitted on their own in step 2.
        bank: flow.investor.bank_submitted_at ? `Submitted ${fmtWhen(flow.investor.bank_submitted_at)}` : 'Not submitted yet',
        nominees: flow.investor.nominees_submitted_at ? `Submitted ${fmtWhen(flow.investor.nominees_submitted_at)}` : 'Not submitted yet',
        ack: flow.investor.acknowledged_at ? `Completed ${fmtWhen(flow.investor.acknowledged_at)}` : 'Not completed yet',
    };
    const tileSub: Record<Tab, string> = {
        kyc: (() => { const c = count(flow.kyc_fields); return `${c.ver}/${c.total} fields verified`; })(),
        bank: (() => { const c = count(flow.bank_fields); return `${c.ver}/${c.total} fields verified`; })(),
        nominees: `${unitsVerified}/${flow.units.length} units verified`,
        ack: (() => { const req = ackItems(flow); return `${req.filter(st => st === 'done').length}/${req.length} acknowledged`; })(),
    };
    const tilePct: Record<Tab, number> = {
        kyc: (() => { const c = count(flow.kyc_fields); return c.total ? (c.ver / c.total) * 100 : 0; })(),
        bank: (() => { const c = count(flow.bank_fields); return c.total ? (c.ver / c.total) * 100 : 0; })(),
        nominees: flow.units.length ? (unitsVerified / flow.units.length) * 100 : 0,
        ack: (() => { const req = ackItems(flow); return req.length ? (req.filter(st => st === 'done').length / req.length) * 100 : 0; })(),
    };

    const fieldSection = (section: 'kyc' | 'bank') => {
        const rows = rowsFor(section);
        const byKey = Object.fromEntries(rows.map(r => [r.key, r]));
        const groups = section === 'kyc' ? KYC_GROUPS : BANK_GROUPS;
        return (
            <>
                {section === 'bank' && flow.sections.bank === 'locked' && notYet('Bank details')}
                <section className={`${CARD} overflow-hidden [container-type:inline-size]`}>
                    <div className="flex flex-wrap items-start justify-between gap-2 px-5 py-4">
                        <div><h2 className="text-[15px] font-bold text-slate-900">{section === 'kyc' ? 'KYC details' : 'Bank account details'}</h2>
                            {section === 'bank' && <p className="text-xs text-slate-500 mt-0.5">Payouts go here. Holder name must match KYC.</p>}</div>
                        {/* Header actions: once verified, send the ack link. */}
                        <div className="flex flex-wrap items-center justify-end gap-2">
                            {flow.sections[section] === 'verified' && ackSendButton(flow.acks?.[section]?.status || '', () => sendAck(section))}
                            <AckedChip status={flow.sections[section]} ack={flow.acks?.[section]} />
                        </div>
                    </div>
                    <VerifyHeader />
                    {groups.map(g => (
                        <div key={g.title}>
                            {g.keys.map(k => {
                                const r = byKey[k];
                                if (!r) return null;
                                return (
                                    <VerifyRow key={k} label={FIELD_LABELS[k] || k} value={k === 'dob' ? fmtDate(r.value) : r.value} state={r.verify_state as RowState} mono={MONO.has(k)} remarks={r.remarks}
                                        staged={false} disabled={busy || !reviewable(flow.sections[section]) || locked || !isEmployee} canVerify={isEmployee} canFlag={false}
                                        pendingVerify={pendingVer[k]?.checked}
                                        // Send the value checked, so the history's Verified tab shows it
                                        // (name / email set at signup have no earlier entry to fall back on).
                                        onVerify={checked => stageVerify([{ key: k, checked, current: r.verify_state,
                                            ...(r.value !== null && r.value !== undefined && r.value !== '' ? { value: String(r.value) } : {}) }])}
                                        onToggleFlag={() => undefined}
                                        editDisabled={busy}
                                        // MOU given on any unit: KYC and bank are frozen.
                                        onEdit={flow.mou_locked ? undefined : () => {
                                            const acked = flow.acks?.[section]?.status === 'done';
                                            setEdit({ key: k, label: FIELD_LABELS[k] || k, value: String(r.value ?? ''), reason: '', needsProof: r.verify_state === 'verified' || acked,
                                                reopens: acked, proofUrl: '', proofSource: '' });
                                        }} />
                                );
                            })}
                        </div>
                    ))}
                </section>
            </>
        );
    };

    const nomineeSection = () => {
        if (!flow.units.length) return <div className={`${CARD} p-8 text-center text-slate-500`}>This investor has no units yet — there are no nominees to verify or acknowledge.</div>;
        const items: any[] = unit?.verify_items || [];
        const nomineeIds: string[] = Array.from(new Set(items.filter(i => i.nominee_id).map(i => i.nominee_id)));
        const nomineeOf = (nid: string) => unit?.allocations?.find((a: any) => (a.nominee.id || a.nominee.aadhar_number) === nid);
        return (
            <div className="flex flex-col gap-4">
            {flow.acks?.nominees?.status !== 'done' && ackBar('nominees')}
            {waitingOrders.length > 0 && (
                <section className={`${CARD} px-4 py-3 flex flex-col gap-2`} aria-label="Orders waiting for nominees">
                    <h2 className="text-[13px] font-bold text-slate-900">Orders waiting for nominees</h2>
                    {waitingOrders.map(o => (
                        <div key={o.oid} className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100 first-of-type:border-0 first-of-type:pt-0">
                            <div className="min-w-0 text-[12px]">
                                <span className="font-mono font-semibold text-slate-900">{o.oid}</span>
                                <span className="text-slate-500"> · {o.missing.map((u: any) => `Unit ${u.unit_index + 1}`).join(', ')} without a nominee</span>
                                {o.sent && <span className="text-slate-500"> · <span className="inline-flex items-center gap-1 text-[#128C7E] font-semibold"><WhatsAppLogo size={11} />Sent</span> {fmtWhen(o.sent.sent_at)}</span>}
                            </div>
                            {/* The ank_nominee link for this one order. */}
                            <button type="button" disabled={busy || locked} onClick={() => sendOrderNominee(o.oid)} title={`WhatsApp template “ank_nominee” · ${o.oid}`}
                                className="h-7 px-2.5 rounded-md bg-[#25D366] hover:bg-[#1EBE5A] text-white text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-40">
                                <WhatsAppLogo size={12} />{o.sent ? 'Resend nominee link' : 'Send nominee link'}</button>
                        </div>
                    ))}
                </section>
            )}
            <div className="grid grid-cols-1 xl:grid-cols-[240px_minmax(0,1fr)] gap-4">
                {/* Padding on every side leaves room for the selected card's 2px outline; a
                    sideways-scrolling list clips anything outside its box otherwise. */}
                <nav aria-label="Units" className="flex xl:flex-col gap-2 overflow-x-auto xl:overflow-visible -m-1 p-1">
                    {flow.units.map((u: any) => (
                        <button key={u.unit_id} type="button" onClick={() => setUnitId(u.unit_id)}
                            className={`shrink-0 xl:shrink w-[220px] xl:w-auto text-left px-3.5 py-3 rounded-xl bg-white flex flex-col gap-1 ${u.unit_id === unitId ? 'ring-2 ring-[#1B4332]' : 'ring-1 ring-[#E3E9E5] hover:ring-emerald-300'}`}>
                            {/* Title and status on one line; the details get their own full-width line. */}
                            <span className="flex items-center justify-between gap-2 w-full">
                                <b className="text-sm text-slate-900">Unit {u.unit_index + 1}</b>
                                <span className="shrink-0"><SectionChip status={ackedStatus(u.status, u.ack?.status)} /></span>
                            </span>
                            <span className="text-[11px] leading-snug text-slate-500">
                                {SHOW_MOU_TYPE && <>{u.mou_type ? mouTypeLabel(u.mou_type) : 'No MOU type'} · </>}{u.allocations.length} nominee{u.allocations.length === 1 ? '' : 's'}
                                {SHOW_MOU_TYPE && (u.farmvest?.mou_given === true ? ' · MOU given' : u.farmvest?.mou_given === false ? ' · MOU not given' : '')}
                                {u.ack && u.ack.status !== 'locked' ? ` · Ack: ${ACK_STATUS[u.ack.status]?.label}` : ''}
                            </span>
                        </button>
                    ))}
                </nav>
                {unit && ['no_nominees', 'not_started'].includes(unit.status) && flow.step === 'kyc' && <div className="min-w-0">{notYet('Unit nominees')}</div>}
                {/* No nominee yet: the nominee's fields, each "Not provided" — as KYC and bank show before they're filled. */}
                {unit && unit.status === 'no_nominees' && flow.step !== 'kyc' && (
                    <section className={`${CARD} overflow-hidden [container-type:inline-size] min-w-0`}>
                        <div className="px-5 py-4">
                            {unitTitle(unit)}
                        </div>
                        <VerifyHeader />
                        {['name', 'mobile', 'relation', 'gender', 'aadhar', 'pan', 'doc_aadhar', 'doc_pan'].map(k => (
                            <VerifyRow key={k} label={NOMINEE_FIELD_LABELS[k] || k} value={null} state={'pending' as RowState}
                                staged={false} disabled canFlag={false} onVerify={() => {}} onToggleFlag={() => {}} />
                        ))}
                    </section>
                )}
                {unit && unit.status !== 'no_nominees' && !(flow.step === 'kyc' && unit.status === 'not_started') && (
                    <div className="flex flex-col gap-4 min-w-0">
                        {unit.ack?.status !== 'done' && unitAckBar(unit)}
                        <section className={`${CARD} overflow-hidden [container-type:inline-size]`}>
                            <div className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
                                <div>
                                    {unitTitle(unit)}
                                </div>
                                <div className="flex items-center gap-2">
                                    <AckedChip status={unit.status} ack={unit.ack} />
                                </div>
                            </div>
                            <VerifyHeader />
                            {items.filter(i => !i.nominee_id).map(i => (
                                <VerifyRow key={i.key} label={NOMINEE_FIELD_LABELS[i.field] || 'MOU type'} state={i.state}
                                    // value_type (nominees saved before the MOU type): "<basis>|<value>".
                                    value={i.field === 'value_type' ? `${valueTypeLabel(String(i.value).split('|')[0])} · ${inr(String(i.value).split('|')[1])}` : mouTypeLabel(i.value)}
                                    staged={false} disabled={busy || !reviewable(unit.status) || locked || !isEmployee} canVerify={isEmployee} canFlag={false}
                                    pendingVerify={pendingVer[i.key]?.checked}
                                    onVerify={checked => stageVerify([{ key: i.key, checked, value: i.value, current: i.state }])} onToggleFlag={() => undefined} />
                            ))}
                            {nomineeIds.map(nid => {
                                const a = nomineeOf(nid);
                                return (
                                    <div key={nid}>
                                        <div className="px-5 pt-4 pb-2 border-t border-slate-200 text-[12px] font-extrabold tracking-[.06em] uppercase text-[#1B4332]">
                                            Nominee · {a?.nominee?.nominee_full_name || nid}
                                        </div>
                                        {items.filter(i => i.nominee_id === nid && !['allocation_pct', 'allocated_amount', 'dob', 'address'].includes(i.field)).map(i => (
                                            <VerifyRow key={i.key} label={NOMINEE_FIELD_LABELS[i.field] || i.field}
                                                value={i.value}
                                                state={i.state} mono={['aadhar', 'pan', 'mobile'].includes(i.field)} remarks={i.remarks}
                                                staged={false} disabled={busy || !reviewable(unit.status) || locked || !!openCorrection || !isEmployee} canVerify={isEmployee}
                                                canFlag={false}
                                                pendingVerify={pendingVer[i.key]?.checked}
                                                onVerify={checked => stageVerify([{ key: i.key, checked, value: i.value, current: i.state }])}
                                                onToggleFlag={() => undefined}
                                                editDisabled={busy || !!openCorrection}
                                                onEdit={NOMINEE_EDITABLE.has(i.field) && !unit.mou_locked ? () => {
                                                    const acked = flow.acks?.nominees?.status === 'done';
                                                    setEdit({ key: i.field, label: `Nominee ${NOMINEE_FIELD_LABELS[i.field] || i.field}`, value: String(i.value ?? ''), reason: '',
                                                        needsProof: i.state === 'verified' || acked, reopens: acked, proofUrl: '', proofSource: '',
                                                        nominee: { unitId: unit.unit_id, nomineeId: nid } });
                                                } : undefined} />
                                        ))}
                                    </div>
                                );
                            })}
                        </section>
                        {openCorrection ? (
                            <div className="rounded-2xl p-4 bg-amber-50 ring-1 ring-amber-200 text-sm text-amber-950 flex flex-wrap items-center gap-3">
                                <div className="flex-1 min-w-[220px]"><b>Correction open since {fmtWhen(openCorrection.requested_at)}.</b> <span>“{openCorrection.remarks}”</span></div>
                                <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(`${window.location.origin}/nominee-correction?token=${encodeURIComponent(openCorrection.token)}`); toast('Correction link copied'); } catch { /* ignore */ } }}
                                    className="h-9 px-3 rounded-lg bg-white ring-1 ring-amber-300 text-xs font-bold inline-flex items-center gap-1"><Copy size={13} />Copy link</button>
                                <button type="button" disabled={busy} onClick={() => cancelNomineeCorrection(openCorrection.id)} className="h-9 px-3 rounded-lg text-xs font-bold text-red-700 hover:bg-red-50">Cancel request</button>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
            </div>
        );
    };

    const ackSection = () => {
        return (
            <section className={`${CARD} overflow-hidden [container-type:inline-size]`}>
                <div className="px-5 py-4 flex flex-wrap items-start justify-between gap-3">
                    <div><h2 className="text-[15px] font-bold text-slate-900">Acknowledgment</h2>
                        <p className="text-xs text-slate-500">Once a section is verified, send the investor its acknowledgment link on WhatsApp (ank_verify) — one for KYC, one for bank, and one for each unit's nominee. Any later change asks for it again.</p></div>
                </div>
                {[
                    ...(['kyc', 'bank'] as AckSec[]).map(k => ({ key: k, sec: k, label: ACK_SECTION_LABELS[k], sub: null as string | null,
                        a: flow.acks?.[k] || { status: 'locked' }, verify: flow.sections[k], open: () => setTab(k) })),
                    ...flow.units.map((u: any) => ({ key: u.unit_id, sec: 'nominees' as AckSec, unitId: u.unit_id, label: `Unit ${u.unit_index + 1} nominee`,
                        sub: u.order_id as string | null, a: u.ack || { status: 'locked' }, verify: u.status, open: () => { setUnitId(u.unit_id); setTab('nominees'); } })),
                ].map(r => (
                    <div key={r.key} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100">
                        <div className="min-w-[160px]"><b className="text-sm">{r.label}</b>{r.sub && <span className="ml-1.5 text-[11px] font-mono text-slate-500">{r.sub}</span>}
                            <div className="text-[11px] text-slate-500">{r.a.status === 'done' ? `Acknowledged ${fmtWhen(r.a.acknowledged_at)}` : r.a.status === 'sent' ? `Link sent ${fmtWhen(r.a.sent_at)}` : ''}</div></div>
                        <div className="flex flex-wrap items-center gap-2">
                            <SectionChip status={r.verify} />
                            <span className={`h-6 px-2.5 inline-flex items-center rounded-full text-[11px] font-bold whitespace-nowrap ${ACK_STATUS[r.a.status]?.cls}`}>{ACK_STATUS[r.a.status]?.label}</span>
                            {ackSendButton(r.a.status, () => sendAck(r.sec, (r as any).unitId))}
                            <button type="button" onClick={r.open} className="h-9 px-3 rounded-lg ring-1 ring-slate-200 text-xs font-bold">Open</button>
                        </div>
                    </div>
                ))}
                {!flow.units.length && <div className="px-4 py-3 border-t border-slate-100 text-[12px] text-slate-500">No units — no nominees to acknowledge.</div>}
                <div className="px-4 py-3.5 border-t border-slate-100 text-sm text-slate-600">
                    {flow.sections.ack === 'done' ? `Profile complete${flow.investor.acknowledged_at ? ` · ${fmtWhen(flow.investor.acknowledged_at)}` : ''}.` : 'The profile completes when KYC, bank and every unit above are acknowledged.'}
                </div>
            </section>
        );
    };

    return (
        // The dashboard layout already pads the sides — fill its content area (like the queue)
        // instead of insetting again; min-w-0 lets it shrink to the area, never past it.
        // kyc-verify: larger text on big screens (index.css).
        <div className={`kyc-verify w-full min-w-0 ${pendingCount ? 'pb-28' : 'pb-8 sm:pb-10'}`} aria-busy={refreshing}>
            <Snackbar message={snack.message} type={snack.type} onClose={() => setSnack({ message: null, type: null })} />
            {pendingCount > 0 && isEmployee && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[900] w-[calc(100%-2rem)] max-w-2xl rounded-2xl bg-[#14231B] text-white shadow-xl px-4 py-3 flex flex-wrap items-center gap-3" role="region" aria-label="Unsubmitted verification">
                    <div className="flex-1 min-w-[200px] text-sm">
                        <b>{pendingCount} field{pendingCount === 1 ? '' : 's'} ready to submit</b>
                        <div className="text-xs text-emerald-200">{Object.values(pendingVer).filter(v => v.checked).length} to verify · {Object.values(pendingVer).filter(v => !v.checked).length} to un-verify — recorded as one step under your name</div>
                    </div>
                    <button type="button" disabled={busy} onClick={() => setPendingVer({})} className="h-10 px-3 rounded-xl text-sm font-bold text-emerald-100 hover:bg-white/10 disabled:opacity-40">Discard</button>
                    <button type="button" disabled={busy} onClick={submitVerification} className="h-10 px-4 rounded-xl bg-[#52B788] text-[#0B2618] text-sm font-extrabold inline-flex items-center gap-1.5 disabled:opacity-40">
                        {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCheck size={15} />}Submit verification
                    </button>
                </div>
            )}
            <div className="flex items-center justify-between gap-3">
                <button type="button" onClick={goBack} aria-label="Back to KYC queue" title="Back to KYC queue"
                    className="h-9 pl-2 pr-3 rounded-lg ring-1 ring-slate-200 bg-white text-[13px] font-bold text-slate-700 inline-flex items-center gap-1.5 hover:bg-slate-50 hover:text-[#1B4332] shrink-0">
                    <ArrowLeft size={16} />Back
                </button>
                <button type="button" onClick={refresh} disabled={refreshing} title="Refresh" aria-label="Refresh"
                    className="h-9 pl-2.5 pr-3 rounded-lg ring-1 ring-slate-200 bg-white text-[13px] font-bold text-slate-700 inline-flex items-center gap-1.5 hover:bg-slate-50 hover:text-[#1B4332] shrink-0 disabled:cursor-wait">
                    <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />Refresh
                </button>
            </div>

            {refreshing ? <VerifySkeleton /> : (<>
            <section className={`${CARD} p-4 sm:p-5 mt-3`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 min-w-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <span className="w-11 h-11 rounded-full bg-[#1B4332] text-white grid place-items-center text-sm font-extrabold shrink-0">{name.split(' ').map((x: string) => x[0]).slice(0, 2).join('').toUpperCase()}</span>
                        <div className="min-w-0">
                            <h1 className="text-[17px] font-extrabold text-slate-900 leading-tight">{name}</h1>
                            <div className="text-[12px] text-slate-500 truncate">
                                <span className="font-mono">{withCountryCode(mobile, flow.investor.country_code)}</span> · Created {createdDate(flow.investor.created_at)}
                            </div>
                        </div>
                    </div>
                    {/* Referrer, in its own column beside the investor. */}
                    <div className="min-w-0 sm:pl-6 sm:border-l border-[#E3E9E5]">
                        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Referred by</div>
                        {flow.investor.referred_by ? (<>
                            <div className="text-[14px] font-semibold text-slate-800 leading-tight truncate">{flow.investor.referred_by.name}</div>
                            <div className="text-[12px] text-slate-500 font-mono">{withCountryCode(flow.investor.referred_by.mobile, flow.investor.referred_by.country_code)}</div>
                        </>) : <div className="text-[13px] text-slate-400">—</div>}
                    </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                        <span className={`h-6 px-2.5 inline-flex items-center rounded-full text-[11px] font-bold whitespace-nowrap ${flow.step === 'complete' ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-900'}`}>{STEP_SHORT[flow.step] || flow.step}</span>
                        {flow.reopened && <span className="h-6 px-2.5 inline-flex items-center rounded-full text-[11px] font-bold bg-amber-50 text-amber-900 ring-1 ring-amber-200">Reopened</span>}
                        <button type="button" onClick={() => setFvOpen(true)} title="What was sent to FarmVest for each order" className="h-8 px-2.5 rounded-md ring-1 ring-slate-300 bg-white text-slate-700 text-xs font-bold inline-flex items-center gap-1.5 hover:ring-[#1B4332] hover:text-[#1B4332]"><Database size={12} />FarmVest synced details</button>
                        {isEmployee && <button type="button" disabled={busy || locked} onClick={fillForInvestor} title="For an investor who can't fill in the form themselves" className="h-8 px-2.5 rounded-md bg-[#1B4332] text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-40"><PenLine size={12} />Fill in for investor</button>}
                    </div>
                </div>
            </section>
            <section className={`${CARD} p-4 sm:p-5 mt-3`}>
                {/* KYC done and acknowledged, but nothing to name nominees for yet. */}
                {noOrder && flow.acks?.kyc?.status === 'done' && (
                    <div className="mb-3 px-3.5 py-2.5 rounded-lg bg-amber-50 ring-1 ring-amber-200 text-[13px] text-amber-950" role="note">
                        <b>No order placed yet.</b> The investor needs to place an order before bank details and unit nominees open — Template 2 can be sent after that.
                    </div>
                )}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                    {TABS.map(t => {
                        const closed = tabLocked(t.key);
                        const st = closed ? 'locked' : t.key === 'ack' ? (flow.acks ? flow.sections.ack : 'locked') : flow.sections[t.key];
                        const bar = st === 'needs_correction' ? 'bg-red-600' : tilePct[t.key] >= 100 ? 'bg-emerald-600' : 'bg-[#1B4332]';
                        // The onboarding message for this card: Template 1 (KYC), 2 (bank & nominees, on
                        // the Bank card) or 3 (final confirmation, on Acknowledgment).
                        const msgStep = TILE_TEMPLATE[t.key];
                        const m = msgStep ? (flow.step_messages || []).find((x: any) => x.step === msgStep) : null;
                        return (
                            // The whole card opens its section; only the WhatsApp button inside does something else.
                            <div key={t.key} onClick={() => { if (!closed) setTab(t.key); }}
                                className={`rounded-xl flex flex-col transition ${closed ? 'ring-1 ring-[#E3E9E5] bg-slate-50 cursor-not-allowed' : `cursor-pointer ${tab === t.key ? 'ring-2 ring-[#1B4332] bg-white' : 'ring-1 ring-[#E3E9E5] bg-white hover:ring-emerald-300'}`}`}>
                            <button type="button" onClick={e => { e.stopPropagation(); setTab(t.key); }} disabled={closed}
                                title={closed ? lockReason(t.key) : undefined}
                                className={`flex-1 text-left px-3 py-2.5 flex flex-col gap-2 rounded-xl ${closed ? 'cursor-not-allowed opacity-70' : ''}`}>
                                <span className="flex items-center justify-between gap-2 w-full">
                                    <b className={`text-[13px] inline-flex items-center gap-1.5 ${closed ? 'text-slate-500' : 'text-slate-900'}`}>{closed && <Lock size={13} />}{t.label}</b>
                                    {closed
                                        ? <span className="h-6 px-2.5 inline-flex items-center rounded-full text-[11px] font-bold bg-slate-100 text-slate-500 ring-1 ring-slate-200">Locked</span>
                                        : <SectionChip status={t.key === 'ack' ? st : ackedStatus(st, flow.acks?.[t.key]?.status)} />}
                                </span>
                                <span className="h-1 rounded-full bg-slate-100 overflow-hidden"><span className={`block h-full rounded-full ${bar}`} style={{ width: `${tilePct[t.key]}%` }} /></span>
                                <span className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
                                    <span>{closed ? lockReason(t.key) : tileSub[t.key]}</span>
                                    <span className={`text-right ${/^(Submitted|Completed|Collected)/.test(tileWhen[t.key]) ? 'font-semibold text-slate-700' : 'text-slate-400'}`}>{tileWhen[t.key]}</span>
                                </span>
                            </button>
                            {m && (m.sent_at || m.state === 'current') && (
                                <div className="flex flex-wrap items-center justify-between gap-2 px-3 pb-2.5 pt-2 border-t border-slate-100 text-[11px]">
                                    <span className="min-w-0 text-slate-500">
                                        {m.sent_at
                                            ? <span className="inline-flex items-center gap-1"><span className="inline-flex items-center gap-1 text-[#128C7E] font-semibold">{m.sent_via === 'whatsapp' ? <WhatsAppLogo size={11} /> : null}{m.sent_via === 'copied' ? 'Copied' : 'Sent'}</span>{fmtWhen(m.sent_at)}</span>
                                            : m.step === 2 && noOrder ? <>Template 2 · waits for an order</> : <>Template {m.step} · not sent yet</>}
                                    </span>
                                    {/* The current step's message, or an earlier one already sent (re-send). */}
                                    {(m.state === 'current' || (m.state === 'done' && m.sent_at)) && (m.whatsapp_template ? (
                                        <button type="button" disabled={busy || locked || (m.step === 2 && noOrder)} onClick={e => { e.stopPropagation(); sendStep(m.step); }}
                                            title={m.step === 2 && noOrder ? 'The investor needs to place an order first' : `WhatsApp template “${m.whatsapp_template}”`}
                                            className="h-7 px-2.5 rounded-md bg-[#25D366] hover:bg-[#1EBE5A] text-white text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-40">
                                            <WhatsAppLogo size={12} />{m.sent_at ? 'Resend on WhatsApp' : 'Send on WhatsApp'}</button>
                                    ) : (
                                        <button type="button" disabled={busy || locked} onClick={e => { e.stopPropagation(); sendStep(m.step); }}
                                            className="h-7 px-2.5 rounded-md bg-[#1B4332] text-white text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-40">
                                            <MessageCircle size={12} />{m.sent_at ? 'Copy again' : 'Copy message'}</button>
                                    ))}
                                </div>
                            )}
                            </div>
                        );
                    })}
                </div>
            </section>


            <div className="mt-4">
                <div className="flex flex-col gap-4 min-w-0">
                    {tab === 'kyc' && fieldSection('kyc')}
                    {tab === 'bank' && fieldSection('bank')}
                    {tab === 'nominees' && nomineeSection()}
                    {tab === 'ack' && ackSection()}
                    {/* This section's own history (KYC, Bank or Nominees) under its table. */}
                    {tab !== 'ack' && (
                        <KycHistoryCard book={flow.history_book} fieldAudit={flow.field_audit} section={tab}
                            sectionStatus={flow.sections} units={flow.units}
                            investorName={[flow.investor?.first_name, flow.investor?.last_name].filter(Boolean).join(' ')} mobile={mobile}
                            addedBy={flow.investor?.added_by} addedAt={flow.investor?.created_at} />
                    )}
                </div>
            </div>

            {fvOpen && <FarmvestSyncModal mobile={mobile} onClose={closeFv} />}
            {edit && (
                <div className="fixed inset-0 z-[1000] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="edit-title">
                    <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[92vh] overflow-y-auto">
                        <div className="flex items-center justify-between"><h3 id="edit-title" className="font-bold">Edit {edit.label}</h3>
                            <button type="button" aria-label="Close" onClick={() => setEdit(null)} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-slate-100"><X size={16} /></button></div>
                        {edit.needsProof && (
                            <p className="mt-2 px-3 py-2 rounded-lg bg-amber-50 text-amber-950 text-xs"><b>This field is verified.</b> Change it only on the client's request, and attach what they sent.{edit.reopens ? ' The investor will be asked to confirm this section again.' : ''}</p>
                        )}
                        {edit.key.startsWith('doc_') ? (
                            <div className="mt-3"><DocUpload id="edit-val" label="New document" required url={edit.value || null} mobile={mobile} folder={edit.nominee ? 'nominees' : 'kyc'}
                                onChange={u => setEdit({ ...edit, value: u || '' })} /></div>
                        ) : EDIT_OPTIONS[edit.key] && edit.nominee ? (
                            <>
                                <label htmlFor="edit-val" className="block text-sm font-semibold text-slate-700 mt-3 mb-1">New value</label>
                                <select id="edit-val" value={edit.value} onChange={e => setEdit({ ...edit, value: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-slate-300 text-sm outline-none focus:border-[#1B4332] bg-white">
                                    {!EDIT_OPTIONS[edit.key].includes(edit.value) && <option value={edit.value}>{edit.value || 'Choose…'}</option>}
                                    {EDIT_OPTIONS[edit.key].map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                                {edit.key === 'relation' && edit.value === 'Guardian' && <p className="text-xs text-amber-800 mt-1">Guardian needs a guardian certificate on file — upload it first if there isn't one.</p>}
                            </>
                        ) : (
                            <>
                                <label htmlFor="edit-val" className="block text-sm font-semibold text-slate-700 mt-3 mb-1">New value</label>
                                <input id="edit-val" value={edit.value} onChange={e => setEdit({ ...edit, value: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-slate-300 text-sm outline-none focus:border-[#1B4332]" />
                            </>
                        )}
                        <fieldset className="mt-3">
                            <legend className="block text-sm font-semibold text-slate-700 mb-1">Client sent the change by{edit.needsProof ? ' (required)' : ''}</legend>
                            <div className="flex flex-wrap gap-2">
                                {PROOF_SOURCES.map(p => (
                                    <button key={p.key} type="button" aria-pressed={edit.proofSource === p.key} onClick={() => setEdit({ ...edit, proofSource: p.key })}
                                        className={`h-9 px-3 rounded-lg text-xs font-bold ${edit.proofSource === p.key ? 'bg-[#1B4332] text-white' : 'ring-1 ring-slate-300 text-slate-700 bg-white'}`}>{p.label}</button>
                                ))}
                            </div>
                        </fieldset>
                        <div className="mt-3">
                            <DocUpload id="edit-proof" label={`Proof from the client — screenshot of their email or message${edit.needsProof ? '' : ' (optional)'}`} required={edit.needsProof}
                                url={edit.proofUrl || null} mobile={mobile} folder="kyc-edit-proof" onChange={u => setEdit({ ...edit, proofUrl: u || '' })} />
                        </div>
                        <label htmlFor="edit-reason" className="block text-sm font-semibold text-slate-700 mt-3 mb-1">Reason for edit (required)</label>
                        <input id="edit-reason" value={edit.reason} onChange={e => setEdit({ ...edit, reason: e.target.value })} placeholder="e.g. Client emailed their new address" className="w-full h-11 px-3 rounded-xl border border-slate-300 text-sm outline-none focus:border-[#1B4332]" />
                        <p className="text-xs text-slate-500 mt-2">The old value, your name, the reason and the proof are kept in the history. The field goes back to unverified.</p>
                        <div className="flex justify-end gap-2 mt-4">
                            <button type="button" onClick={() => setEdit(null)} className="h-10 px-4 rounded-lg ring-1 ring-slate-200 text-sm font-semibold">Cancel</button>
                            <button type="button" disabled={busy || edit.reason.trim().length < 3 || !edit.value || (edit.needsProof && !(edit.proofUrl && edit.proofSource)) || (!!edit.proofUrl && !edit.proofSource)} onClick={saveEdit} className="h-10 px-4 rounded-lg bg-[#1B4332] text-white text-sm font-semibold disabled:opacity-40">Save edit</button>
                        </div>
                    </div>
                </div>
            )}
            </>)}
        </div>
    );
};

export default KycVerifyPage;
